import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Switch,
  FormControlLabel,
  Slider
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Bolt as ForceIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import Header from '../Header';
import SpinWheelLiveHostTab from './SpinWheelLiveHostTab';
import SpinWheelHotSeatTab from './SpinWheelHotSeatTab';
import SliceWedgePreview from './SliceWedgePreview';
import PrizeCircleImage from '../../Common/PrizeCircleImage';
import io from 'socket.io-client';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const SpinningWheelDashboard = ({
  initialTab = 0,
  showTabNavigation = true,
  showHeader = true,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [forceBusy, setForceBusy] = useState(false);
  const [swForceActive, setSwForceActive] = useState(false);
  
  // Wheels data
  const [wheels, setWheels] = useState([]);
  const [selectedWheel, setSelectedWheel] = useState(null);
  
  // Analytics data
  const [overview, setOverview] = useState(null);
  const [spins, setSpins] = useState([]);
  const [spinsPage, setSpinsPage] = useState(0);
  const [spinsRowsPerPage, setSpinsRowsPerPage] = useState(25);
  const [spinsTotal, setSpinsTotal] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState({
    dateRange: 'all',
    userType: 'all',
    startDate: '',
    endDate: ''
  });
  
  // Dialogs
  const [wheelDialogOpen, setWheelDialogOpen] = useState(false);
  const [sliceDialogOpen, setSliceDialogOpen] = useState(false);
  const [editingWheel, setEditingWheel] = useState(null);
  const [editingSlice, setEditingSlice] = useState(null);
  
  // Form states
  const [wheelForm, setWheelForm] = useState({
    wheel_name: '',
    description: '',
    logo_url: '',
    background_image_url: '',
    audio_url: '',
    is_active: false,
    number_of_slices: 8
  });
  
  const [sliceForm, setSliceForm] = useState({
    slice_order: 1,
    text: '',
    color: '#FF6B6B',
    points: 0,
    image_url: '',
    background_image_url: '',
    image_zoom: 0.6,
    image_rotation: 0,
    image_offset_x: 0,
    image_offset_y: 0,
    modal_image_zoom: 1,
    modal_image_offset_x: 0,
    modal_image_offset_y: 0,
    modal_image_rotation: 0,
  });

  const [sliceImageUploading, setSliceImageUploading] = useState(false);
  const [sliceBgImageUploading, setSliceBgImageUploading] = useState(false);

  // Get auth token
  const getAuthToken = () => {
    return localStorage.getItem('auth_token') || localStorage.getItem('admin_token');
  };

  const loadSwForceActive = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/settings/public/mym-titles-config`);
      if (!res.ok) return;
      const data = await res.json();
      const forced =
        Array.isArray(data.config) &&
        data.config.find((item) => item.forceModal && item.id === 'spinning_wheel');
      setSwForceActive(!!forced);
    } catch {
      setSwForceActive(false);
    }
  }, []);

  useEffect(() => {
    loadSwForceActive();
  }, [loadSwForceActive]);

  useEffect(() => {
    const socket = io(API_BASE, { transports: ['websocket'] });
    socket.on('mym.force_open', (payload) => {
      if (payload?.id === 'spinning_wheel') setSwForceActive(true);
      else loadSwForceActive();
    });
    socket.on('mym.force_clear', () => setSwForceActive(false));
    return () => socket.disconnect();
  }, [loadSwForceActive]);

  const handleForceJoin = async () => {
    if (forceBusy) return;
    setForceBusy(true);
    try {
      const authHeader = { Authorization: `Bearer ${getAuthToken()}` };
      const getRes = await fetch(`${API_BASE}/api/settings/public/mym-titles-config`, {
        headers: authHeader,
      });
      const getData = await getRes.json().catch(() => ({}));
      if (!getRes.ok || !Array.isArray(getData.config)) {
        throw new Error('Failed to load MYM titles config');
      }
      const nextConfig = getData.config.map((item) => {
        const isSw = item.id === 'spinning_wheel';
        return {
          ...item,
          forceModal: isSw,
          enabled: isSw ? true : !!item.enabled,
          hidden: isSw ? false : !!item.hidden,
        };
      });
      const putRes = await fetch(`${API_BASE}/api/settings/mym-titles-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify({ config: nextConfig }),
      });
      const putData = await putRes.json().catch(() => ({}));
      if (!putRes.ok) {
        throw new Error(putData.message || putData.error || 'Failed to force join');
      }
      setSwForceActive(true);
      enqueueSnackbar('Force Join active — users are locked to Spin the Wheel', {
        variant: 'success',
      });
    } catch (e) {
      enqueueSnackbar(e.message || 'Failed to force join', { variant: 'error' });
    } finally {
      setForceBusy(false);
    }
  };

  const handleForceStop = async () => {
    if (forceBusy) return;
    setForceBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/settings/mym-force-stop`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to stop force');
      }
      setSwForceActive(false);
      enqueueSnackbar('Force mode stopped', { variant: 'success' });
    } catch (e) {
      enqueueSnackbar(e.message || 'Failed to stop force', { variant: 'error' });
    } finally {
      setForceBusy(false);
    }
  };
  
  // Fetch all wheels
  const fetchWheels = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_BASE}/api/spinning-wheel/wheels?includeInactive=true`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch wheels');
      
      const data = await response.json();
      setWheels(data.wheels || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Fetch wheel details
  const fetchWheelDetails = async (wheelId) => {
    try {
      const response = await fetch(`${API_BASE}/api/spinning-wheel/wheels/${wheelId}`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch wheel details');
      
      const data = await response.json();
      setSelectedWheel(data.wheel);
    } catch (err) {
      setError(err.message);
    }
  };

  // Upload slice image (label or background)
  const handleSliceImageUpload = async (e, field) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;

    const looksLikeImage = (file.type || '').startsWith('image/') || /\.(jpe?g|png|gif|webp|bmp|svg|tiff?|ico|avif|heic|heif|jfif|apng|jxl)$/i.test(file.name);
    if (!looksLikeImage) {
      enqueueSnackbar('Please choose an image file.', { variant: 'error' });
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      enqueueSnackbar('Image must be 20 MB or smaller.', { variant: 'error' });
      return;
    }

    field === 'image_url' ? setSliceImageUploading(true) : setSliceBgImageUploading(true);

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await axios.post(`${API_BASE}/api/upload/quiz`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        maxBodyLength: Infinity,
        maxContentLength: Infinity,
      });

      const imageUrl = response.data.imageUrl;
      setSliceForm(prev => ({
        ...prev,
        [field]: imageUrl
      }));

      enqueueSnackbar('Image uploaded successfully!', { variant: 'success' });
    } catch (error) {
      console.error('Error uploading image:', error);
      const serverMessage = error.response?.data?.error;
      enqueueSnackbar(serverMessage || 'Error uploading image. Please try again.', { variant: 'error' });
    } finally {
      field === 'image_url' ? setSliceImageUploading(false) : setSliceBgImageUploading(false);
    }
  };
  
  // Fetch overview analytics
  const fetchOverview = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.userType !== 'all') params.append('userType', filters.userType);
      if (selectedWheel) params.append('wheel_id', selectedWheel.wheel_id);

      const response = await fetch(`${API_BASE}/api/spinning-wheel/admin/analytics/overview?${params}`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch overview');
      
      const data = await response.json();
      setOverview(data.analytics);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters, selectedWheel]);
  
  // Fetch spins
  const fetchSpins = useCallback(async (page = 0, rowsPerPage = 25) => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString()
      });
      
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.userType !== 'all') params.append('userType', filters.userType);
      if (selectedWheel) params.append('wheel_id', selectedWheel.wheel_id);

      const response = await fetch(`${API_BASE}/api/spinning-wheel/admin/spins?${params}`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch spins');
      
      const data = await response.json();
      setSpins(data.spins || []);
      setSpinsTotal(data.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters, selectedWheel]);
  
  // Handle date range change
  const handleDateRangeChange = (range) => {
    const today = new Date();
    let startDate = '';
    let endDate = '';

    if (range === 'today') {
      startDate = today.toISOString().split('T')[0];
      endDate = today.toISOString().split('T')[0];
    } else if (range === 'week') {
      const weekAgo = new Date(today);
      weekAgo.setDate(today.getDate() - 7);
      startDate = weekAgo.toISOString().split('T')[0];
      endDate = today.toISOString().split('T')[0];
    } else if (range === 'month') {
      const monthAgo = new Date(today);
      monthAgo.setMonth(today.getMonth() - 1);
      startDate = monthAgo.toISOString().split('T')[0];
      endDate = today.toISOString().split('T')[0];
    }

    setFilters(prev => ({
      ...prev,
      dateRange: range,
      startDate,
      endDate
    }));
  };
  
  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setSpinsPage(newPage);
    fetchSpins(newPage, spinsRowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setSpinsRowsPerPage(newRowsPerPage);
    setSpinsPage(0);
    fetchSpins(0, newRowsPerPage);
  };
  
  // Open wheel dialog
  const handleOpenWheelDialog = (wheel = null) => {
    if (wheel) {
      setEditingWheel(wheel);
      setWheelForm({
        wheel_name: wheel.wheel_name || '',
        description: wheel.description || '',
        logo_url: wheel.logo_url || '',
        background_image_url: wheel.background_image_url || '',
        audio_url: wheel.audio_url || '',
        is_active: wheel.is_active === 1,
        number_of_slices: wheel.slices?.length || 8
      });
    } else {
      setEditingWheel(null);
      setWheelForm({
        wheel_name: '',
        description: '',
        logo_url: '',
        background_image_url: '',
        audio_url: '',
        is_active: false,
        number_of_slices: 8
      });
    }
    setWheelDialogOpen(true);
  };
  
  // Save wheel
  const handleSaveWheel = async () => {
    try {
      setLoading(true);
      const url = editingWheel
        ? `${API_BASE}/api/spinning-wheel/wheels/${editingWheel.wheel_id}`
        : `${API_BASE}/api/spinning-wheel/wheels`;
      
      const method = editingWheel ? 'PUT' : 'POST';
      
      // Don't send number_of_slices to backend (it's only used for creating slices)
      const { number_of_slices, ...wheelDataToSend } = wheelForm;
      const body = wheelDataToSend;
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`
        },
        body: JSON.stringify(body)
      });
      
      if (!response.ok) throw new Error('Failed to save wheel');
      
      const data = await response.json();
      const wheelId = editingWheel ? editingWheel.wheel_id : data.wheel_id;
      
      // If creating a new wheel and number_of_slices is specified, create placeholder slices
      if (!editingWheel && number_of_slices && number_of_slices > 0) {
        const colors = [
          '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F',
          '#BB8FCE', '#85C1E2', '#F8B739', '#E74C3C', '#3498DB', '#2ECC71',
          '#9B59B6', '#1ABC9C', '#E67E22', '#34495E'
        ];
        
        // Create slices
        for (let i = 1; i <= number_of_slices; i++) {
          const color = colors[(i - 1) % colors.length];
          await fetch(`${API_BASE}/api/spinning-wheel/wheels/${wheelId}/slices`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${getAuthToken()}`
            },
            body: JSON.stringify({
              slice_order: i,
              text: `Slice ${i}`,
              color: color,
              points: 0,
              image_url: ''
            })
          });
        }
      }
      
      enqueueSnackbar(`Wheel ${editingWheel ? 'updated' : 'created'} successfully!${!editingWheel && number_of_slices ? ` Created ${number_of_slices} placeholder slices.` : ''}`, {
        variant: 'success',
        autoHideDuration: 4000
      });
      
      setWheelDialogOpen(false);
      fetchWheels();
      
      // If we just created a wheel, fetch its details to show slices
      if (!editingWheel) {
        await fetchWheelDetails(wheelId);
        setSelectedWheel(null); // Reset to show the new wheel
      }
    } catch (err) {
      setError(err.message);
      enqueueSnackbar(err.message, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };
  
  // Delete wheel
  const handleDeleteWheel = async (wheelId) => {
    if (!window.confirm('Are you sure you want to delete this wheel? This will also delete all slices and spins.')) {
      return;
    }
    
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/spinning-wheel/wheels/${wheelId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to delete wheel');
      
      enqueueSnackbar('Wheel deleted successfully!', {
        variant: 'success',
        autoHideDuration: 3000
      });
      
      fetchWheels();
      if (selectedWheel?.wheel_id === wheelId) {
        setSelectedWheel(null);
      }
    } catch (err) {
      setError(err.message);
      enqueueSnackbar(err.message, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };
  
  // Toggle wheel active status
  const handleToggleActive = async (wheel) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/spinning-wheel/wheels/${wheel.wheel_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`
        },
        body: JSON.stringify({
          is_active: wheel.is_active === 1 ? 0 : 1
        })
      });
      
      if (!response.ok) throw new Error('Failed to update wheel');
      
      enqueueSnackbar(`Wheel ${wheel.is_active === 1 ? 'deactivated' : 'activated'}!`, {
        variant: 'success',
        autoHideDuration: 3000
      });
      
      fetchWheels();
      if (selectedWheel?.wheel_id === wheel.wheel_id) {
        await fetchWheelDetails(wheel.wheel_id);
      }
    } catch (err) {
      setError(err.message);
      enqueueSnackbar(err.message, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };
  
  // Open slice dialog
  const handleOpenSliceDialog = (slice = null) => {
    if (slice) {
      setEditingSlice(slice);
      setSliceForm({
        slice_order: slice.slice_order || 1,
        text: slice.text || '',
        color: slice.color || '#FF6B6B',
        points: slice.points || 0,
        image_url: slice.image_url || '',
        background_image_url: slice.background_image_url || '',
        image_zoom: slice.image_zoom ?? 0.6,
        image_rotation: slice.image_rotation ?? 0,
        image_offset_x: slice.image_offset_x ?? 0,
        image_offset_y: slice.image_offset_y ?? 0,
        modal_image_zoom: slice.modal_image_zoom ?? 1,
        modal_image_offset_x: slice.modal_image_offset_x ?? 0,
        modal_image_offset_y: slice.modal_image_offset_y ?? 0,
        modal_image_rotation: slice.modal_image_rotation ?? 0,
      });
    } else {
      setEditingSlice(null);
      const nextOrder = selectedWheel?.slices?.length ? Math.max(...selectedWheel.slices.map(s => s.slice_order)) + 1 : 1;
      setSliceForm({
        slice_order: nextOrder,
        text: '',
        color: '#FF6B6B',
        points: 0,
        image_url: '',
        background_image_url: '',
        image_zoom: 0.6,
        image_rotation: 0,
        image_offset_x: 0,
        image_offset_y: 0,
        modal_image_zoom: 1,
        modal_image_offset_x: 0,
        modal_image_offset_y: 0,
        modal_image_rotation: 0,
      });
    }
    setSliceDialogOpen(true);
  };
  
  // Save slice
  const handleSaveSlice = async () => {
    if (!selectedWheel) {
      enqueueSnackbar('Please select a wheel first', { variant: 'warning' });
      return;
    }
    
    try {
      setLoading(true);
      const url = editingSlice
        ? `${API_BASE}/api/spinning-wheel/slices/${editingSlice.slice_id}`
        : `${API_BASE}/api/spinning-wheel/wheels/${selectedWheel.wheel_id}/slices`;
      
      const method = editingSlice ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`
        },
        body: JSON.stringify(sliceForm)
      });
      
      if (!response.ok) throw new Error('Failed to save slice');
      
      enqueueSnackbar(`Slice ${editingSlice ? 'updated' : 'created'} successfully!`, {
        variant: 'success',
        autoHideDuration: 3000
      });
      
      setSliceDialogOpen(false);
      await fetchWheelDetails(selectedWheel.wheel_id);
    } catch (err) {
      setError(err.message);
      enqueueSnackbar(err.message, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };
  
  // Delete slice
  const handleDeleteSlice = async (sliceId) => {
    if (!window.confirm('Are you sure you want to delete this slice?')) {
      return;
    }
    
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/spinning-wheel/slices/${sliceId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to delete slice');
      
      enqueueSnackbar('Slice deleted successfully!', {
        variant: 'success',
        autoHideDuration: 3000
      });
      
      await fetchWheelDetails(selectedWheel.wheel_id);
    } catch (err) {
      setError(err.message);
      enqueueSnackbar(err.message, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };
  
  // Load data on mount and tab change
  useEffect(() => {
    if (activeTab === 1) {
      fetchWheels();
    } else if (activeTab === 3) {
      fetchOverview();
    } else if (activeTab === 4) {
      fetchSpins(spinsPage, spinsRowsPerPage);
    }
  }, [activeTab, filters, fetchWheels, fetchOverview, fetchSpins, spinsPage, spinsRowsPerPage]);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);
  
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <>
      {showHeader ? <Header /> : null}
      <Container maxWidth="xl" sx={{ mt: showHeader ? 4 : 1, pb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
            Spinning Wheel - Management
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {swForceActive ? (
              <Button
                variant="contained"
                color="error"
                startIcon={forceBusy ? <CircularProgress size={16} color="inherit" /> : <StopIcon />}
                onClick={handleForceStop}
                disabled={forceBusy}
              >
                Force Stop
              </Button>
            ) : (
              <Button
                variant="contained"
                color="secondary"
                startIcon={forceBusy ? <CircularProgress size={16} color="inherit" /> : <ForceIcon />}
                onClick={handleForceJoin}
                disabled={forceBusy}
              >
                Force
              </Button>
            )}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Filters */}
        {(activeTab === 3 || activeTab === 4) && (
          <Paper sx={{ p: 2, mb: 3, bgcolor: '#1E1E1E' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Date Range</InputLabel>
                  <Select
                    value={filters.dateRange}
                    label="Date Range"
                    onChange={(e) => handleDateRangeChange(e.target.value)}
                  >
                    <MenuItem value="all">All Time</MenuItem>
                    <MenuItem value="today">Today</MenuItem>
                    <MenuItem value="week">Last 7 Days</MenuItem>
                    <MenuItem value="month">Last 30 Days</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>User Type</InputLabel>
                  <Select
                    value={filters.userType}
                    label="User Type"
                    onChange={(e) => handleFilterChange('userType', e.target.value)}
                  >
                    <MenuItem value="all">All Users</MenuItem>
                    <MenuItem value="registered">Registered</MenuItem>
                    <MenuItem value="guest">Guest</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Start Date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="End Date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              {activeTab === 3 && (
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Wheel</InputLabel>
                    <Select
                      value={selectedWheel?.wheel_id || ''}
                      label="Wheel"
                      onChange={(e) => {
                        const wheel = wheels.find(w => w.wheel_id === e.target.value);
                        setSelectedWheel(wheel || null);
                      }}
                    >
                      <MenuItem value="">All Wheels</MenuItem>
                      {wheels.map(wheel => (
                        <MenuItem key={wheel.wheel_id} value={wheel.wheel_id}>
                          {wheel.wheel_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </Grid>
          </Paper>
        )}

        {showTabNavigation ? (
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{ mb: 3 }}
            variant="fullWidth"
          >
            <Tab label="Users Wheel" />
            <Tab label="Default Wheels" />
            <Tab label="Hot seat" />
            <Tab label="Analytics" />
            <Tab label="Spins" />
          </Tabs>
        ) : null}

        {loading && activeTab !== 4 && activeTab !== 2 && activeTab !== 0 && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
            <Typography variant="h6" sx={{ ml: 2 }}>
              Loading...
            </Typography>
          </Box>
        )}

        <Box sx={{ mt: 3 }}>
          {/* Users Wheel Tab */}
          {activeTab === 0 && <SpinWheelLiveHostTab />}

          {/* Hot seat Tab */}
          {activeTab === 2 && <SpinWheelHotSeatTab />}

          {/* Default Wheels Tab */}
          {activeTab === 1 && (
            <>
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenWheelDialog()}
                >
                  Create New Wheel
                </Button>
              </Box>

              {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                  <CircularProgress />
                </Box>
              ) : (
                <Paper sx={{ bgcolor: '#1E1E1E' }}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Wheel Name</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Total Spins</TableCell>
                          <TableCell>Created At</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {wheels.map((wheel) => (
                          <TableRow key={wheel.wheel_id}>
                            <TableCell>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {wheel.wheel_name}
                              </Typography>
                              {wheel.description && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mt: 0.5, maxWidth: 360, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                >
                                  {wheel.description}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={wheel.is_active === 1 ? 'Active' : 'Inactive'}
                                color={wheel.is_active === 1 ? 'success' : 'default'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>{wheel.total_spins || 0}</TableCell>
                            <TableCell>
                              {wheel.created_at ? formatDate(wheel.created_at) : 'N/A'}
                            </TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<EditIcon />}
                                  onClick={() => {
                                    handleOpenWheelDialog(wheel);
                                    fetchWheelDetails(wheel.wheel_id);
                                  }}
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={wheel.is_active === 1 ? <StopIcon /> : <PlayIcon />}
                                  onClick={() => handleToggleActive(wheel)}
                                  color={wheel.is_active === 1 ? 'warning' : 'success'}
                                >
                                  {wheel.is_active === 1 ? 'Deactivate' : 'Activate'}
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  startIcon={<DeleteIcon />}
                                  onClick={() => handleDeleteWheel(wheel.wheel_id)}
                                >
                                  Delete
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => {
                                    setSelectedWheel(wheel);
                                    fetchWheelDetails(wheel.wheel_id);
                                  }}
                                >
                                  Manage Slices
                                </Button>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                        {wheels.length === 0 && !loading && (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              <Typography variant="body1" color="text.secondary">
                                No wheels found. Create your first wheel!
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}

              {/* Selected Wheel Slices Management */}
              {selectedWheel && (
                <Paper sx={{ p: 3, mt: 4, bgcolor: '#1E1E1E' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      Slices for: {selectedWheel.wheel_name}
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => handleOpenSliceDialog()}
                    >
                      Add Slice
                    </Button>
                  </Box>
                  
                  {selectedWheel.slices && selectedWheel.slices.length > 0 ? (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Order</TableCell>
                            <TableCell>Text</TableCell>
                            <TableCell>Color</TableCell>
                            <TableCell>Label Image</TableCell>
                            <TableCell>Background Image</TableCell>
                            <TableCell>Points</TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedWheel.slices.map((slice) => (
                            <TableRow key={slice.slice_id}>
                              <TableCell>{slice.slice_order}</TableCell>
                              <TableCell>{slice.text}</TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box
                                    sx={{
                                      width: 30,
                                      height: 30,
                                      backgroundColor: slice.color,
                                      borderRadius: 1,
                                      border: '1px solid #333'
                                    }}
                                  />
                                  {slice.color}
                                </Box>
                              </TableCell>
                              <TableCell>
                                {slice.image_url ? (
                                  <Box
                                    component="img"
                                    src={slice.image_url.startsWith('http') ? slice.image_url : `${API_BASE}${slice.image_url}`}
                                    alt={slice.text || `Slice ${slice.slice_order}`}
                                    sx={{
                                      width: 48,
                                      height: 48,
                                      borderRadius: 1,
                                      objectFit: 'cover',
                                      border: '1px solid #333'
                                    }}
                                  />
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    No image
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {slice.background_image_url ? (
                                  <Box
                                    component="img"
                                    src={slice.background_image_url.startsWith('http') ? slice.background_image_url : `${API_BASE}${slice.background_image_url}`}
                                    alt={slice.text || `Slice ${slice.slice_order} background`}
                                    sx={{
                                      width: 48,
                                      height: 48,
                                      borderRadius: 1,
                                      objectFit: 'cover',
                                      border: '1px solid #333'
                                    }}
                                  />
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    No image
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>{slice.points}</TableCell>
                              <TableCell>
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenSliceDialog(slice)}
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteSlice(slice.slice_id)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No slices yet. Add your first slice!
                    </Typography>
                  )}
                </Paper>
              )}
            </>
          )}

          {/* Analytics Tab */}
          {activeTab === 3 && overview && (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: '#1E1E1E' }}>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Total Spins
                    </Typography>
                    <Typography variant="h4">
                      {overview.total_spins || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: '#1E1E1E' }}>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Unique Players
                    </Typography>
                    <Typography variant="h4">
                      {overview.unique_devices || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: '#1E1E1E' }}>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Total Points Awarded
                    </Typography>
                    <Typography variant="h4">
                      {overview.total_points_awarded || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: '#1E1E1E' }}>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Average Points per Spin
                    </Typography>
                    <Typography variant="h4">
                      {overview.average_points_per_spin ? overview.average_points_per_spin.toFixed(2) : '0.00'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Spins Tab */}
          {activeTab === 4 && (
            <>
              {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <TableContainer component={Paper} sx={{ bgcolor: '#1E1E1E' }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Spin ID</TableCell>
                          <TableCell>Wheel</TableCell>
                          <TableCell>User</TableCell>
                          <TableCell>Slice</TableCell>
                          <TableCell>Points</TableCell>
                          <TableCell>Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {spins.map((spin) => (
                          <TableRow key={spin.spin_id}>
                            <TableCell>{spin.spin_id}</TableCell>
                            <TableCell>{spin.wheel_name}</TableCell>
                            <TableCell>
                              {spin.user_type === 'registered'
                                ? `${spin.fname || ''} ${spin.lname || ''}`.trim() || 'User'
                                : spin.guest_name || 'Guest'}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                  sx={{
                                    width: 20,
                                    height: 20,
                                    backgroundColor: spin.slice_color,
                                    borderRadius: 0.5,
                                    border: '1px solid #333'
                                  }}
                                />
                                {spin.slice_text}
                              </Box>
                            </TableCell>
                            <TableCell>{spin.points_awarded}</TableCell>
                            <TableCell>{formatDate(spin.created_at)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={spinsTotal}
                    page={spinsPage}
                    onPageChange={handleChangePage}
                    rowsPerPage={spinsRowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                  />
                </>
              )}
            </>
          )}
        </Box>

        {/* Wheel Dialog */}
        <Dialog
          open={wheelDialogOpen}
          onClose={() => setWheelDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {editingWheel ? 'Edit Wheel' : 'Create New Wheel'}
            <IconButton
              onClick={() => setWheelDialogOpen(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Wheel Name"
                  value={wheelForm.wheel_name}
                  onChange={(e) => setWheelForm({ ...wheelForm, wheel_name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={wheelForm.description}
                  onChange={(e) => setWheelForm({ ...wheelForm, description: e.target.value })}
                  multiline
                  rows={3}
                />
              </Grid>
              {!editingWheel && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Number of Slices"
                    type="number"
                    value={wheelForm.number_of_slices}
                    onChange={(e) => setWheelForm({ ...wheelForm, number_of_slices: parseInt(e.target.value) || 8 })}
                    inputProps={{ min: 2, max: 32 }}
                    helperText="Number of slices to create (2-32)"
                  />
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Logo URL"
                  value={wheelForm.logo_url}
                  onChange={(e) => setWheelForm({ ...wheelForm, logo_url: e.target.value })}
                  placeholder="URL or path to logo image"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Background Image URL"
                  value={wheelForm.background_image_url}
                  onChange={(e) => setWheelForm({ ...wheelForm, background_image_url: e.target.value })}
                  placeholder="URL or path to background image"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Audio URL"
                  value={wheelForm.audio_url}
                  onChange={(e) => setWheelForm({ ...wheelForm, audio_url: e.target.value })}
                  placeholder="URL or path to audio file"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={wheelForm.is_active}
                      onChange={(e) => setWheelForm({ ...wheelForm, is_active: e.target.checked })}
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setWheelDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSaveWheel}
              disabled={!wheelForm.wheel_name || loading}
              startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Slice Dialog */}
        <Dialog
          open={sliceDialogOpen}
          onClose={() => setSliceDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {editingSlice ? 'Edit Slice' : 'Create New Slice'}
            <IconButton
              onClick={() => setSliceDialogOpen(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Slice Order"
                  type="number"
                  value={sliceForm.slice_order}
                  onChange={(e) => setSliceForm({ ...sliceForm, slice_order: parseInt(e.target.value) || 1 })}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Points"
                  type="number"
                  value={sliceForm.points}
                  onChange={(e) => setSliceForm({ ...sliceForm, points: parseInt(e.target.value) || 0 })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Text (optional label)"
                  value={sliceForm.text}
                  onChange={(e) => setSliceForm({ ...sliceForm, text: e.target.value })}
                  helperText="Shown on the wheel along with the image. Leave empty to show only the image."
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Color"
                  type="color"
                  value={sliceForm.color}
                  onChange={(e) => setSliceForm({ ...sliceForm, color: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Label Image URL"
                  value={sliceForm.image_url}
                  onChange={(e) => setSliceForm({ ...sliceForm, image_url: e.target.value })}
                  placeholder="Optional image URL for label image"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Background Image URL"
                  value={sliceForm.background_image_url}
                  onChange={(e) => setSliceForm({ ...sliceForm, background_image_url: e.target.value })}
                  placeholder="Optional background image URL for this slice"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Button
                  variant="outlined"
                  component="label"
                  disabled={sliceImageUploading}
                  fullWidth
                >
                  {sliceImageUploading ? 'Uploading label image...' : 'Upload Label Image'}
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => handleSliceImageUpload(e, 'image_url')}
                  />
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <Button
                  variant="outlined"
                  component="label"
                  disabled={sliceBgImageUploading}
                  fullWidth
                >
                  {sliceBgImageUploading ? 'Uploading background image...' : 'Upload Background Image'}
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => handleSliceImageUpload(e, 'background_image_url')}
                  />
                </Button>
              </Grid>

              {/* ── Image Adjustment Controls ── */}
              {(() => {
                const existingSlices = selectedWheel?.slices || [];
                const anySliceHasImage = existingSlices.some(s => s.image_url || s.background_image_url)
                  || sliceForm.image_url || sliceForm.background_image_url;
                const currentHasImage = sliceForm.image_url || sliceForm.background_image_url;
                const otherSlicesWithImages = existingSlices.filter(s => {
                  if (editingSlice && s.slice_id === editingSlice.slice_id) return false;
                  return s.image_url || s.background_image_url;
                });
                if (!anySliceHasImage) return null;
                return (
                  <Grid item xs={12}>
                    <Paper
                      variant="outlined"
                      sx={{ p: 2, mt: 1, bgcolor: 'rgba(255,255,255,0.03)', borderColor: 'rgba(255,255,255,0.12)' }}
                    >
                      <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                        Image Adjustments
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                        The outlined wedge is this slice. All slices with images are shown so you can check consistency.
                      </Typography>

                      <SliceWedgePreview
                        slices={(() => {
                          if (editingSlice) return existingSlices;
                          return [...existingSlices, { color: sliceForm.color }];
                        })()}
                        activeIndex={(() => {
                          if (!editingSlice) return existingSlices.length;
                          const idx = existingSlices.findIndex((s) => s.slice_id === editingSlice.slice_id);
                          return idx >= 0 ? idx : 0;
                        })()}
                        imageUrl={(() => {
                          const img = sliceForm.background_image_url || sliceForm.image_url;
                          if (!img) return '';
                          return img.startsWith('http') ? img : `${API_BASE}${img}`;
                        })()}
                        color={sliceForm.color}
                        zoom={sliceForm.image_zoom ?? 0.6}
                        rotation={sliceForm.image_rotation ?? 0}
                        offsetX={sliceForm.image_offset_x ?? 0}
                        offsetY={sliceForm.image_offset_y ?? 0}
                      />

                      {currentHasImage && (
                        <Grid container spacing={2}>
                          {/* Zoom */}
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">
                              Zoom: {(sliceForm.image_zoom ?? 0.6).toFixed(2)}
                            </Typography>
                            <Slider
                              size="small"
                              value={sliceForm.image_zoom ?? 0.6}
                              min={0.05}
                              max={4}
                              step={0.01}
                              onChange={(_, v) => setSliceForm(prev => ({ ...prev, image_zoom: v }))}
                              sx={{ color: '#FFD700' }}
                            />
                          </Grid>

                          {/* Rotation */}
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">
                              Rotation: {Math.round(sliceForm.image_rotation ?? 0)}°
                            </Typography>
                            <Slider
                              size="small"
                              value={sliceForm.image_rotation ?? 0}
                              min={0}
                              max={360}
                              step={1}
                              onChange={(_, v) => setSliceForm(prev => ({ ...prev, image_rotation: v }))}
                              sx={{ color: '#4ECDC4' }}
                            />
                          </Grid>

                          {/* Offset X */}
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">
                              Offset X: {Math.round(sliceForm.image_offset_x ?? 0)}px
                            </Typography>
                            <Slider
                              size="small"
                              value={sliceForm.image_offset_x ?? 0}
                              min={-400}
                              max={400}
                              step={1}
                              onChange={(_, v) => setSliceForm(prev => ({ ...prev, image_offset_x: v }))}
                              sx={{ color: '#FF6B6B' }}
                            />
                          </Grid>

                          {/* Offset Y */}
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">
                              Offset Y: {Math.round(sliceForm.image_offset_y ?? 0)}px
                            </Typography>
                            <Slider
                              size="small"
                              value={sliceForm.image_offset_y ?? 0}
                              min={-400}
                              max={400}
                              step={1}
                              onChange={(_, v) => setSliceForm(prev => ({ ...prev, image_offset_y: v }))}
                              sx={{ color: '#BB8FCE' }}
                            />
                          </Grid>

                          {/* Copy from another slice */}
                          {otherSlicesWithImages.length > 0 && (
                            <Grid item xs={12} sm={6}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Copy settings from</InputLabel>
                                <Select
                                  value=""
                                  label="Copy settings from"
                                  onChange={(e) => {
                                    const srcSlice = existingSlices.find(s => s.slice_id === e.target.value);
                                    if (srcSlice) {
                                      setSliceForm(prev => ({
                                        ...prev,
                                        image_zoom: srcSlice.image_zoom ?? 0.6,
                                        image_rotation: srcSlice.image_rotation ?? 0,
                                        image_offset_x: srcSlice.image_offset_x ?? 0,
                                        image_offset_y: srcSlice.image_offset_y ?? 0,
                                      }));
                                      enqueueSnackbar(`Copied settings from Slice ${srcSlice.slice_order}`, { variant: 'info', autoHideDuration: 2000 });
                                    }
                                  }}
                                >
                                  {otherSlicesWithImages.map(s => (
                                    <MenuItem key={s.slice_id} value={s.slice_id}>
                                      Slice {s.slice_order}{s.text ? ` – ${s.text}` : ''}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                          )}

                          {/* Apply to all slices */}
                          {otherSlicesWithImages.length > 0 && (
                            <Grid item xs={12} sm={6}>
                              <Button
                                size="small"
                                variant="outlined"
                                fullWidth
                                sx={{ height: 40, textTransform: 'none', borderColor: '#FFD700', color: '#FFD700', '&:hover': { borderColor: '#fff', color: '#fff' } }}
                                onClick={async () => {
                                  const adjustments = {
                                    image_zoom: sliceForm.image_zoom ?? 0.6,
                                    image_rotation: sliceForm.image_rotation ?? 0,
                                    image_offset_x: sliceForm.image_offset_x ?? 0,
                                    image_offset_y: sliceForm.image_offset_y ?? 0,
                                  };
                                  try {
                                    setLoading(true);
                                    await Promise.all(otherSlicesWithImages.map(s =>
                                      fetch(`${API_BASE}/api/spinning-wheel/slices/${s.slice_id}`, {
                                        method: 'PUT',
                                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getAuthToken()}` },
                                        body: JSON.stringify(adjustments),
                                      })
                                    ));
                                    enqueueSnackbar(`Applied to ${otherSlicesWithImages.length} other slice(s)`, { variant: 'success', autoHideDuration: 3000 });
                                    await fetchWheelDetails(selectedWheel.wheel_id);
                                  } catch (err) {
                                    enqueueSnackbar('Failed to apply to other slices', { variant: 'error' });
                                  } finally {
                                    setLoading(false);
                                  }
                                }}
                                disabled={loading}
                              >
                                Apply to all slices with images
                              </Button>
                            </Grid>
                          )}

                          {/* Reset button */}
                          <Grid item xs={12}>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => setSliceForm(prev => ({
                                ...prev,
                                image_zoom: 0.6,
                                image_rotation: 0,
                                image_offset_x: 0,
                                image_offset_y: 0
                              }))}
                              sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'none' }}
                            >
                              Reset wheel image
                            </Button>
                          </Grid>
                        </Grid>
                      )}

                      <Box sx={{ mt: currentHasImage ? 2.5 : 0, pt: currentHasImage ? 2 : 0, borderTop: currentHasImage ? '1px solid rgba(255,255,255,0.08)' : 'none' }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                          Prize modal circle
                        </Typography>
                        {currentHasImage ? (
                          <>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                              {sliceForm.background_image_url
                                ? 'Uses the background image.'
                                : 'No background image, so this uses the label image.'}
                              {' '}This framing is only for the circle in the winning prize modal.
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
                              <PrizeCircleImage
                                src={sliceForm.background_image_url || sliceForm.image_url}
                                zoom={sliceForm.modal_image_zoom ?? 1}
                                rotation={sliceForm.modal_image_rotation ?? 0}
                                offsetX={sliceForm.modal_image_offset_x ?? 0}
                                offsetY={sliceForm.modal_image_offset_y ?? 0}
                                size={160}
                              />
                            </Box>
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary">
                                  Zoom: {(sliceForm.modal_image_zoom ?? 1).toFixed(2)}
                                </Typography>
                                <Slider
                                  size="small"
                                  value={sliceForm.modal_image_zoom ?? 1}
                                  min={0.3}
                                  max={8}
                                  step={0.01}
                                  onChange={(_, v) => setSliceForm(prev => ({ ...prev, modal_image_zoom: v }))}
                                  sx={{ color: '#FFD700' }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary">
                                  Rotation: {Math.round(sliceForm.modal_image_rotation ?? 0)}°
                                </Typography>
                                <Slider
                                  size="small"
                                  value={sliceForm.modal_image_rotation ?? 0}
                                  min={0}
                                  max={360}
                                  step={1}
                                  onChange={(_, v) => setSliceForm(prev => ({ ...prev, modal_image_rotation: v }))}
                                  sx={{ color: '#4ECDC4' }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary">
                                  Offset X: {Math.round(sliceForm.modal_image_offset_x ?? 0)}px
                                </Typography>
                                <Slider
                                  size="small"
                                  value={sliceForm.modal_image_offset_x ?? 0}
                                  min={-220}
                                  max={220}
                                  step={1}
                                  onChange={(_, v) => setSliceForm(prev => ({ ...prev, modal_image_offset_x: v }))}
                                  sx={{ color: '#FF6B6B' }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary">
                                  Offset Y: {Math.round(sliceForm.modal_image_offset_y ?? 0)}px
                                </Typography>
                                <Slider
                                  size="small"
                                  value={sliceForm.modal_image_offset_y ?? 0}
                                  min={-220}
                                  max={220}
                                  step={1}
                                  onChange={(_, v) => setSliceForm(prev => ({ ...prev, modal_image_offset_y: v }))}
                                  sx={{ color: '#BB8FCE' }}
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => setSliceForm(prev => ({
                                    ...prev,
                                    modal_image_zoom: 1,
                                    modal_image_rotation: 0,
                                    modal_image_offset_x: 0,
                                    modal_image_offset_y: 0,
                                  }))}
                                  sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'none' }}
                                >
                                  Reset circle
                                </Button>
                              </Grid>
                            </Grid>
                          </>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            No background or label image, so the prize modal will not show a picture.
                          </Typography>
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                );
              })()}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSliceDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSaveSlice}
              disabled={loading || !sliceForm.slice_order || !sliceForm.color}
              startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

export default SpinningWheelDashboard;
